/**
 * BankItemEditorDialog — create or edit a question bank item.
 * Supports MCQ + True/False (v1 scope).
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   bankId     string
 *   item       full bank item object when editing, null when creating
 *   onSaved    (item) => void   parent reloads list afterwards
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { CheckCircle2, Plus, Trash2, Loader2 } from 'lucide-react';

const TF_OPTIONS = () => [
  { _uid: uid(), text: 'True', is_correct: true },
  { _uid: uid(), text: 'False', is_correct: false },
];

const BLANK_MCQ = () => [
  { _uid: uid(), text: '', is_correct: true },
  { _uid: uid(), text: '', is_correct: false },
  { _uid: uid(), text: '', is_correct: false },
  { _uid: uid(), text: '', is_correct: false },
];

// Stable id for React keys on editor option rows — keeps focus when adding /
// removing options mid-list (array indices would otherwise collapse and
// steal focus from the wrong <input>).
function uid() {
  return `o_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

function defaultsFromItem(item) {
  if (!item) {
    return {
      question_type: 'mcq',
      question_text: '',
      options: BLANK_MCQ(),
      multi_select: false,
      max_chars: '',
      required: false,
      explanation: '',
      points: 1,
      difficulty: 'medium',
      bloom_level: 'understand',
      language: 'en',
      tags: '',
    };
  }
  const correctCount = (item.options || []).filter(o => o.is_correct).length;
  return {
    question_type: item.question_type || 'mcq',
    question_text: item.question_text || '',
    options: (item.options || []).map(o => ({ _uid: uid(), text: o.text || '', is_correct: !!o.is_correct })),
    multi_select: !!item.multi_select || correctCount > 1,
    max_chars: item.max_chars ?? '',
    required: !!item.required,
    explanation: item.explanation || '',
    points: item.points ?? 1,
    difficulty: item.difficulty || 'medium',
    bloom_level: item.bloom_level || 'understand',
    language: item.language || 'en',
    tags: (item.tags || []).join(', '),
  };
}

export default function BankItemEditorDialog({ open, onClose, bankId, item, onSaved }) {
  const { post, put } = useApi();
  const isEdit = !!item;
  const [form, setForm] = useState(() => defaultsFromItem(item));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(defaultsFromItem(item));
  }, [open, item]);

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const setOpt = (i, patch) =>
    setForm(prev => ({ ...prev, options: prev.options.map((o, idx) => idx === i ? { ...o, ...patch } : o) }));

  const isMulti = form.question_type === 'mcq' && form.multi_select;

  const markCorrect = (i) => {
    setForm(prev => ({
      ...prev,
      options: prev.options.map((o, idx) =>
        prev.multi_select && prev.question_type === 'mcq'
          ? (idx === i ? { ...o, is_correct: !o.is_correct } : o)   // toggle (checkbox)
          : { ...o, is_correct: idx === i }                          // radio
      ),
    }));
  };

  const setAnswerMode = (multi) => {
    setForm(prev => {
      if (multi === prev.multi_select) return prev;
      let options = prev.options;
      if (!multi) {
        // Back to single: keep only the first correct option
        let kept = false;
        options = prev.options.map(o => {
          if (o.is_correct && !kept) { kept = true; return o; }
          return { ...o, is_correct: false };
        });
      }
      return { ...prev, multi_select: multi, options };
    });
  };

  const addOption = () =>
    update({ options: [...form.options, { _uid: uid(), text: '', is_correct: false }] });

  const removeOption = (i) =>
    update({ options: form.options.filter((_, idx) => idx !== i) });

  const switchType = (newType) => {
    if (newType === 'true_false') {
      update({ question_type: 'true_false', options: TF_OPTIONS(), multi_select: false });
    } else if (newType === 'free_text') {
      update({ question_type: 'free_text', options: [], multi_select: false });
    } else {
      update({ question_type: 'mcq', options: BLANK_MCQ() });
    }
  };

  const isFreeText = form.question_type === 'free_text';
  const hasCorrect = form.options.some(o => o.is_correct);
  const canSave =
    form.question_text.trim().length > 0
    && (isFreeText || (
      form.options.length >= 2
      && form.options.every(o => o.text.trim().length > 0)
      && hasCorrect
    ));

  const handleSave = async () => {
    setSaving(true);
    try {
      const tagsArr = form.tags
        .split(',').map(t => t.trim()).filter(Boolean).slice(0, 20);
      const payload = {
        question_text: form.question_text.trim(),
        points: Number(form.points) || 1,
        explanation: form.explanation.trim() || null,
        options: isFreeText ? null : form.options.map(o => ({ text: o.text.trim(), is_correct: !!o.is_correct })),
        multi_select: form.question_type === 'mcq' ? !!form.multi_select : false,
        max_chars: isFreeText && form.max_chars ? Number(form.max_chars) : null,
        required: isFreeText ? !!form.required : false,
        difficulty: form.difficulty,
        bloom_level: form.bloom_level,
        language: form.language,
        tags: tagsArr,
      };
      let saved;
      if (isEdit) {
        saved = await put(`/question-banks/${bankId}/items/${item.item_id}`, payload);
        toast.success(`Saved (v${saved.version})`);
      } else {
        saved = await post(`/question-banks/${bankId}/items`, {
          ...payload,
          question_type: form.question_type,
        });
        toast.success('Item created (draft)');
      }
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bank-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {isEdit ? `Edit item (creates v${(item.version || 1) + 1})` : 'New bank item'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Editing creates a new immutable version. Prior versions remain visible in History.'
              : 'Add a multiple-choice or true/false question to this bank.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Type — locked when editing because version chain depends on it */}
          <div className="space-y-1.5">
            <Label>Question type</Label>
            {isEdit ? (
              <div className="text-sm border rounded-md px-3 py-2 bg-muted/40 text-muted-foreground">
                {form.question_type === 'true_false' ? 'True / False'
                  : form.question_type === 'free_text' ? 'Free text'
                  : 'Multiple choice (MCQ)'}
                <span className="ml-2 text-[10px]">· type is immutable across versions</span>
              </div>
            ) : (
              <div className="flex gap-2" data-testid="qtype-picker">
                <Button
                  type="button"
                  variant={form.question_type === 'mcq' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => switchType('mcq')}
                  data-testid="qtype-mcq"
                >
                  Multiple choice
                </Button>
                <Button
                  type="button"
                  variant={form.question_type === 'true_false' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => switchType('true_false')}
                  data-testid="qtype-tf"
                >
                  True / False
                </Button>
                <Button
                  type="button"
                  variant={form.question_type === 'free_text' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => switchType('free_text')}
                  data-testid="qtype-free-text"
                >
                  Free text
                </Button>
              </div>
            )}
          </div>

          {/* Question text */}
          <div className="space-y-1.5">
            <Label htmlFor="bi-text">Question</Label>
            <Textarea
              id="bi-text"
              value={form.question_text}
              onChange={(e) => update({ question_text: e.target.value })}
              className="min-h-[80px]"
              placeholder="What is the correct procedure to escalate a tier-2 complaint?"
              data-testid="bi-question-text"
            />
          </div>

          {/* Answer mode — MCQ only */}
          {form.question_type === 'mcq' && (
            <div className="space-y-1.5">
              <Label>Answer mode</Label>
              <div className="flex gap-2" data-testid="answer-mode-picker">
                <Button
                  type="button"
                  variant={!form.multi_select ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAnswerMode(false)}
                  data-testid="answer-mode-single"
                >
                  Single correct answer
                </Button>
                <Button
                  type="button"
                  variant={form.multi_select ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAnswerMode(true)}
                  data-testid="answer-mode-multi"
                >
                  Multiple correct answers
                </Button>
              </div>
              {isMulti && (
                <p className="text-[11px] text-muted-foreground">
                  Trainees must select ALL correct options to earn the points.
                </p>
              )}
            </div>
          )}

          {/* Free text — no options; trainer sets limit + mandatory flag */}
          {isFreeText && (
            <div className="space-y-3 rounded-md border p-3 bg-muted/20" data-testid="free-text-settings">
              <p className="text-xs text-muted-foreground">
                Trainees type their answer in a text box. Not auto-evaluated — you grade
                responses manually from the assessment's Review screen.
              </p>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="bi-max-chars" className="text-sm font-normal">Max characters (optional)</Label>
                <Input
                  id="bi-max-chars"
                  type="number" min={10} max={10000}
                  className="w-28 h-8"
                  placeholder="e.g. 500"
                  value={form.max_chars ?? ''}
                  onChange={(e) => update({ max_chars: e.target.value })}
                  data-testid="bi-max-chars"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm">Mandatory</p>
                  <p className="text-[11px] text-muted-foreground">Trainee must answer before submitting</p>
                </div>
                <Switch
                  checked={!!form.required}
                  onCheckedChange={(v) => update({ required: v })}
                  data-testid="bi-required-switch"
                />
              </div>
            </div>
          )}

          {/* Options */}
          {!isFreeText && (
          <div className="space-y-2">
            <Label>{isMulti ? 'Options · tick every correct answer' : 'Options · click the circle to mark correct'}</Label>
            {form.options.map((o, i) => (
              <div key={o._uid} className="flex items-center gap-2" data-testid={`bi-option-row-${i}`}>
                <button
                  type="button"
                  onClick={() => markCorrect(i)}
                  className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 transition ${
                    isMulti ? 'rounded-md' : 'rounded-full'
                  } ${
                    o.is_correct ? 'border-emerald-500 bg-emerald-500' : 'border-muted-foreground/40 hover:border-muted-foreground'
                  }`}
                  title={o.is_correct ? 'Correct answer' : 'Mark correct'}
                  data-testid={`bi-option-correct-${i}`}
                >
                  {o.is_correct && <CheckCircle2 className="w-3 h-3 text-white" />}
                </button>
                <Input
                  value={o.text}
                  onChange={(e) => setOpt(i, { text: e.target.value })}
                  placeholder={`Option ${i + 1}`}
                  disabled={form.question_type === 'true_false'}
                  data-testid={`bi-option-text-${i}`}
                />
                {form.question_type === 'mcq' && form.options.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOption(i)}
                    className="text-red-500"
                    data-testid={`bi-option-remove-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
            {form.question_type === 'mcq' && form.options.length < 10 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addOption}
                data-testid="bi-add-option"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />Add option ({form.options.length}/10)
              </Button>
            )}
            {!hasCorrect && (
              <p className="text-xs text-amber-600" data-testid="bi-no-correct-warning">
                {isMulti ? 'Select at least one correct answer.' : 'Mark the correct answer.'}
              </p>
            )}
          </div>
          )}

          {/* Explanation */}
          <div className="space-y-1.5">
            <Label htmlFor="bi-expl">Explanation (optional)</Label>
            <Textarea
              id="bi-expl"
              value={form.explanation}
              onChange={(e) => update({ explanation: e.target.value })}
              placeholder="Why is this the correct answer?"
              data-testid="bi-explanation"
            />
          </div>

          {/* Metadata grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Points</Label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={form.points}
                onChange={(e) => update({ points: e.target.value })}
                data-testid="bi-points"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Difficulty</Label>
              <Select value={form.difficulty} onValueChange={(v) => update({ difficulty: v })}>
                <SelectTrigger data-testid="bi-difficulty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Bloom level</Label>
              <Select value={form.bloom_level} onValueChange={(v) => update({ bloom_level: v })}>
                <SelectTrigger data-testid="bi-bloom"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="remember">Remember</SelectItem>
                  <SelectItem value="understand">Understand</SelectItem>
                  <SelectItem value="apply">Apply</SelectItem>
                  <SelectItem value="analyze">Analyze</SelectItem>
                  <SelectItem value="evaluate">Evaluate</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => update({ language: v })}>
                <SelectTrigger data-testid="bi-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">Arabic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <Label htmlFor="bi-tags">Tags (comma-separated)</Label>
            <Input
              id="bi-tags"
              value={form.tags}
              onChange={(e) => update({ tags: e.target.value })}
              placeholder="onboarding, tier-1, soft-skills"
              data-testid="bi-tags"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="bi-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave || saving} data-testid="bi-save">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {isEdit ? 'Save new version' : 'Create item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
